import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ApplicationColumn from './ApplicationColumn';
import ConfigureEmailModal from '../Modals/configureEmailModal';
import RenameKanBanColumnModal from '../Modals/renameKanBanColumnModal';
import ConfigureKanbanAutomatizationsModal from '../Modals/configureKanbanAutomatizationsModal';
import { updateColumnName, updateCompanyKanbanSettings, updateEmailSettings } from '../../Services/kanbanService';
import { prepareDataForKanbanColumns } from '../../Helpers/kanbanHelper';
import { updateApplicationStatus } from '../../Services/applicationService';
import { updateKanbanColumnOrder } from '../../Services/applicationService';
import { KanbanUpdateType } from '../../Helpers/enums';
import useCompanyStore from '../../../../stores/companyStore';
import AssignedUserModal from '../Modals/assignedUserModal';
import { AssignedUserType } from '../Modals/assignedUserModal';
import { getAllKanbanResponsiblesJobsSettings } from '../../Services/jobService';
import useUsersStore from '../../../../stores/usersStore';
import { updateApplicationInKanbanColumns } from '../../Helpers/kanbanHelper';

const Kanban = ({ applications, refreshStatus, parsedKanbanSettings, jobId }) => {
  const [columnsInState, setColumnsInState] = useState();
  const [isConfigureEmailModalOpen, setIsConfigureEmailModalOpen] = useState(false);
  const [isRenameKanbanColumnModalOpen, setIsRenameKanbanColumnModalOpen] = useState(false);
  const [isConfigureAutomatizationsModalOpen, setIsConfigureAutomatizationsModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState();
  const [kanbanResponsibles, setKanbanResponsibles] = useState();
  const [kanbanResponsiblesFilteredByColumn, setKanbanResponsiblesFilteredByColumn] = useState();

  const users = useUsersStore((state) => state.users);
  const company = useCompanyStore((state) => state.company);
  const fetchCompany = useCompanyStore((state) => state.fetchCompany);

  useEffect(() => {
    if (jobId) {
      refreshKanbanResponsibles();
    }
  }, [jobId, users]);

  const refreshStatusForKanban = () => {
    refreshKanbanResponsibles();
    //refreshStatus();
  };

  const refreshKanbanResponsibles = () =>
    getAllKanbanResponsiblesJobsSettings(jobId).then((response) => {
      const fetchedResponsibles = response.data;

      const enrichedResponsibles = fetchedResponsibles.map((responsible) => {
        const user = users.find((u) => u.id === responsible.userId);
        return {
          ...responsible,
          username: user?.username || '',
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          photoUrl: user?.photoUrl || null,
        };
      });
      setKanbanResponsibles(enrichedResponsibles);
    });

  useEffect(() => {
    if (!parsedKanbanSettings) return;

    const result = prepareDataForKanbanColumns(parsedKanbanSettings, applications);
    const sortedResult = sortAllColumns(result); // This only sorts items within columns, not columns themselves
    setColumnsInState({ ...sortedResult });
  }, [applications /*,parsedKanbanSettings*/, company]);

  const toggleIsConfigureEmailModalOpen = () => {
    setIsConfigureEmailModalOpen(!isConfigureEmailModalOpen);
  };

  const toggleIsRenameKanbanModalOpen = () => {
    setIsRenameKanbanColumnModalOpen(!isRenameKanbanColumnModalOpen);
  };

  const toggleIsConfigureKanbanModalOpen = () => {
    setIsConfigureAutomatizationsModalOpen(!isConfigureAutomatizationsModalOpen);
  };

  const toggleAssignedUserModal = (columnId) => {
    if (!isUserModalOpen) {
      setKanbanResponsiblesFilteredByColumn(
        kanbanResponsibles?.filter((responsible) => responsible.kanbanColumnId === columnId)
      );
    }

    setIsUserModalOpen(!isUserModalOpen);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    handleDrop(source, destination, result);
  };

  const sortItems = (items) => {
    return items.sort((a, b) => {
      if (a.kanbanColumnIndex != null && b.kanbanColumnIndex != null) {
        return a.kanbanColumnIndex - b.kanbanColumnIndex;
      } else {
        let dateA = new Date(a.created),
          dateB = new Date(b.created);
        return dateA - dateB;
      }
    });
  };

  const sortAllColumns = (data) => {
    const columns = data;
    for (let key in columns) {
      if (columns[key].items && Array.isArray(columns[key].items)) {
        columns[key].items = sortItems(columns[key].items);
      }
    }
    return data;
  };

  const handleDrop = (source, destination, result) => {
    if (source.droppableId !== destination.droppableId) {
      handleDropInDifferentColumn(source, destination, result);
    } else {
      handleDropInTheSameColumn(source, destination);
    }
  };

  const handleDropInTheSameColumn = (source, destination) => {
    // Clone the current state to avoid direct mutation
    const newState = { ...columnsInState };

    // Extract and clone the items from the source column
    const sourceColumnItems = [...newState[source.droppableId].items];

    // Reorder the items
    const [reorderedItem] = sourceColumnItems.splice(source.index, 1);
    sourceColumnItems.splice(destination.index, 0, reorderedItem);

    // Update the newState with the reordered items
    newState[source.droppableId].items = sourceColumnItems;

    // Update the state with the new newState
    setColumnsInState(newState);

    // Additional logic for updating the backend and other operations
    const updatedOrder = sourceColumnItems.map((item, index) => ({
      applicationId: item.applicationId,
      index: index,
    }));

    updateKanbanColumnOrder(updatedOrder)
      .then(() => {
        // console.log('Order updated in backend');
      })
      .catch((error) => {
        console.error('Error updating order in backend', error);
      });
  };

  const setIsDroppedToFalse = (applicationId) => {
    // Clone the current state to avoid direct mutation
    const newState = JSON.parse(JSON.stringify(columnsInState));

    // Iterate over each column and its items
    for (const columnKey in newState) {
      const column = newState[columnKey];
      if (column.items && column.items.length) {
        column.items.forEach((item) => {
          if (item.applicationId === applicationId) {
            item.isDropped = false; // Update the isDropped property
            item.showCountdownLayer = true;
          }
        });
      }
    }

    // Update the state with the new data
    setColumnsInState(newState);
  };

  const handleDropInDifferentColumn = async (source, destination, result) => {
    const previousState = { ...columnsInState };

    try {
      // 1. Prepare the new state with optimistic update
      const newState = { ...columnsInState };
      const sourceColumn = newState[source.droppableId];
      const destColumn = newState[destination.droppableId];
      const sourceItems = [...sourceColumn.items];
      const destItems = [...destColumn.items];

      // 2. Update items arrays
      const [removed] = sourceItems.splice(source.index, 1);
      removed.isDropped = true;
      destItems.splice(destination.index, 0, removed);

      // 3. Create mapped array for backend
      const mappedArray = destItems.map((application, index) => ({
        applicationId: application.applicationId,
        index: index,
      }));

      // 4. Update local state optimistically
      newState[source.droppableId] = {
        ...sourceColumn,
        items: sourceItems,
      };
      newState[destination.droppableId] = {
        ...destColumn,
        items: destItems,
      };
      setColumnsInState(newState);

      // 5. Call backend updates
      await updateKanbanApplicationStatus(
        result.destination.droppableId,
        result.source.droppableId,
        result.draggableId,
        mappedArray
      );
    } catch (error) {
      // 6. Revert to previous state on error
      setColumnsInState(previousState);
      console.error('Failed to move item between columns:', error);
    }
  };

  const updateKanbanApplicationStatus = async (newStatus, oldStatus, applicationId, mappedArray) => {
    const newStatusColumn = columnsInState[newStatus];
    const oldStatusColumn = columnsInState[oldStatus];
    const sendAutomatedEmail = newStatusColumn['sendAutomatedEmail'];

    // 1. Prepare email data
    const emailData = sendAutomatedEmail
      ? {
          emailContent: newStatusColumn['emailContent'],
          emailSubject: newStatusColumn['emailSubject'],
        }
      : null;

    const application = applications.find((app) => app.applicationId === Number(applicationId));

    if (!application) {
      console.error('Application not found:', applicationId);
      return;
    }

    const jobTitle = application?.jobTitle;
    const applicantFullName = `${application?.applicantFirstName} ${application?.applicantLastName}`;

    // 2. Update backend only
    const response = await updateApplicationStatus(
      applicationId,
      newStatusColumn.id,
      newStatusColumn.title,
      oldStatusColumn.id,
      oldStatusColumn.title,
      sendAutomatedEmail,
      emailData?.emailSubject,
      emailData?.emailContent,
      mappedArray,
      jobId,
      jobTitle,
      applicantFullName
    );

    // TODO: Uncomment this when adding back last action info
    // if (response?.data) {
    //   const updatedApplication = response.data;
    //   updateApplicationInColumns(applicationId, updatedApplication);
    // }
  };

  const updateApplicationInColumns = (applicationId, updatedValues) => {
    setColumnsInState((prevState) => {
      // Deep clone to avoid mutating state directly
      const newState = JSON.parse(JSON.stringify(prevState));
      for (const columnKey in newState) {
        const column = newState[columnKey];
        if (Array.isArray(column.items)) {
          column.items = column.items.map((item) =>
            item.applicationId === applicationId ? { ...item, ...updatedValues } : item
          );
        }
      }
      return newState;
    });
  };

  const updateTitle = async (columnId, newTitle) => {
    const previousTitle = columnsInState[columnId].title;

    try {
      // 1. Optimistically update UI
      setColumnsInState((prevState) => ({
        ...prevState,
        [columnId]: {
          ...prevState[columnId],
          title: newTitle,
        },
      }));

      // 2. Close modal
      toggleIsRenameKanbanModalOpen();

      // 3. Update backend
      await updateColumnName(company?.id, columnId, newTitle);

      // 4. Fetch fresh data and wait for it
      const freshData = await fetchCompany();

      // 5. Update local state with fresh data
      const result = prepareDataForKanbanColumns(JSON.parse(freshData?.kanbanSettings), applications);
      setColumnsInState(result);
    } catch (error) {
      // Revert on error
      setColumnsInState((prevState) => ({
        ...prevState,
        [columnId]: {
          ...prevState[columnId],
          title: previousTitle,
        },
      }));
    }
  };

  const updateEmailSettingsLocal = (columnId, sendAutomatedEmail, emailSubject, emailContent) => {
    updateEmailSettings(company?.id, columnId, sendAutomatedEmail, emailSubject, emailContent).then((response) => {
      setColumnsInState((prevState) => ({
        ...prevState,
        [columnId]: {
          ...prevState[columnId],
          sendAutomatedEmail: sendAutomatedEmail,
          emailContent: emailContent,
          emailSubject: emailSubject,
        },
      }));

      toggleIsConfigureEmailModalOpen();
    });
  };

  const onDragEndColumn = async (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    const previousState = { ...columnsInState };

    try {
      // 1. Get visible columns and sort them
      const visibleColumns = Object.entries(columnsInState)
        .filter(([_, column]) => column.visible)
        .sort((a, b) => a[1].order - b[1].order);

      // 2. Perform optimistic reorder
      const [removed] = visibleColumns.splice(source.index, 1);
      visibleColumns.splice(destination.index, 0, removed);

      // 3. Create new state with updated orders
      const reorderedColumns = { ...columnsInState };
      visibleColumns.forEach(([key, value], newIndex) => {
        // Only update the order property, keep the same object reference for items
        reorderedColumns[key] = { ...reorderedColumns[key], order: newIndex };
      });

      // 4. Update UI optimistically
      setColumnsInState(reorderedColumns);

      // 5. Transform and update backend
      const transformedSettings = Object.entries(reorderedColumns).map(([_, value]) => ({
        Id: value.id,
        Order: value.order,
      }));

      const stringifiedSettings = JSON.stringify(transformedSettings);
      await updateCompanyKanbanSettings(KanbanUpdateType.Order, company.id, stringifiedSettings);
    } catch (error) {
      // Revert on error
      setColumnsInState(previousState);
      console.error('Failed to update column order:', error);
    }
  };

  return (
    <div className="kanban-outer-container">
      <RenameKanBanColumnModal
        isRenameKanbanModalOpen={isRenameKanbanColumnModalOpen}
        toggleIsRenameKanbanModalOpen={() => toggleIsRenameKanbanModalOpen()}
        selectedColumn={selectedColumn}
        updateTitle={(columnId, newTitle) => updateTitle(columnId, newTitle)}
      />
      <ConfigureEmailModal
        isEmailModalOpen={isConfigureEmailModalOpen}
        toggleIsEmailModalOpen={() => toggleIsConfigureEmailModalOpen()}
        selectedColumn={selectedColumn}
        updateEmailSettings={(columnId, sendAutomatedEmail, emailSubject, emailContent) =>
          updateEmailSettingsLocal(columnId, sendAutomatedEmail, emailSubject, emailContent)
        }
      />
      <ConfigureKanbanAutomatizationsModal
        isConfigureAutomatizationsModalOpen={isConfigureAutomatizationsModalOpen}
        toggleIsConfigureAutomatizationsModalOpen={() => toggleIsConfigureKanbanModalOpen()}
        selectedColumn={selectedColumn}
      />
      <AssignedUserModal
        showModal={isUserModalOpen}
        onToggle={(application) => toggleAssignedUserModal(application)}
        onStatusRefresh={() => refreshStatusForKanban()}
        assignedUserType={AssignedUserType.AssignedToKanbanColumn}
        jobId={jobId}
        selectedColumn={selectedColumn}
        required={false}
        preselectedOptions={kanbanResponsiblesFilteredByColumn}
      />
      {
        <DragDropContext
          onDragEnd={(result) => {
            if (result.type === 'column') {
              onDragEndColumn(result);
            } else {
              onDragEnd(result);
            }
          }}
        >
          <div className="kanban-scroll-container">
            <Droppable droppableId="all-columns" direction="horizontal" type="column">
              {(provided) => (
                <div className="kanban-columns-container" {...provided.droppableProps} ref={provided.innerRef}>
                  {columnsInState &&
                    Object.entries(columnsInState)
                      ?.filter(([_, column]) => column.visible)
                      .sort((a, b) => a[1].order - b[1].order)
                      .map(([columnId, column], index) => {
                        // Filter responsibles for this column
                        const columnResponsibles =
                          kanbanResponsibles?.filter((r) => r.kanbanColumnId === column.id) || [];

                        return (
                          <Draggable key={columnId} draggableId={columnId} index={index}>
                            {(dragProvided) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                              >
                                <Droppable droppableId={column.id} type="card">
                                  {(dropProvided, dropSnapshot) => (
                                    <ApplicationColumn
                                      column={column}
                                      columnId={column.id}
                                      provided={dropProvided}
                                      refreshStatus={refreshStatus}
                                      toggleIsConfigureEmailModalOpen={toggleIsConfigureEmailModalOpen}
                                      toggleIsRenameKanbanModalOpen={toggleIsRenameKanbanModalOpen}
                                      toggleIsConfigureAutomatizationsModalOpen={toggleIsConfigureKanbanModalOpen}
                                      toggleAssignedUserModal={() => toggleAssignedUserModal(column.id)}
                                      setSelectedColumn={setSelectedColumn}
                                      parsedKanbanSettings={parsedKanbanSettings}
                                      setIsDroppedToFalse={setIsDroppedToFalse}
                                      kanbanResponsibles={columnResponsibles}
                                    />
                                  )}
                                </Droppable>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </DragDropContext>
      }
    </div>
  );
};

export default Kanban;
